import { COACHING_TIER_RANK, COACHING_TIERS, type CoachingTier } from './constants';

/**
 * Which features belong to which plan (Free, Success Pro, Success Elite).
 *
 * One table replaces what used to be three separate mechanisms: two Settings
 * switches (`chart_guidance_pro_only`, `tools_pro_only`), a handful of Pro
 * gates written into the routes, and three numeric limits. The tenant decides
 * in Admin -> Coaching -> Features; nothing here is a code decision any more.
 *
 * A feature carries a MINIMUM tier rather than a tick per plan, so a higher
 * plan always includes everything a lower one has. "Pro but not Elite" is not
 * expressible, which is the point. `off` is the fourth value, because a tenant
 * that wants nobody to have a feature (today's `streak_freeze_pro = false`)
 * cannot say that with a minimum alone.
 */

export const COACHING_FEATURE_TIERS = ['free', 'pro', 'elite', 'off'] as const;
export type CoachingFeatureTier = (typeof COACHING_FEATURE_TIERS)[number];

/** Features that are on or off per plan. */
export const COACHING_ACCESS_KEYS = [
  'journal_reflection',
  'affirmation_generation',
  'action_suggestions',
  'evening_ai_summary',
  'weekly_ai_summary',
  'chart',
  'guidance',
  'karma',
  'aura',
  'palmistry',
  'breathwork',
  'frequencies',
  'biorhythm',
  'mantras',
  'meditation',
  'biorhythm_spiritual',
  'biorhythm_lucky',
  'vedic_glossary',
  'mood_history',
  'miracle_wins',
  'manifestation_toolkit',
  'future_self',
  'alignment_todos',
  'streak_freeze',
  'prompt_library',
  'programs',
] as const;
export type CoachingAccessKey = (typeof COACHING_ACCESS_KEYS)[number];

/** Features that are a number per plan rather than a switch. */
export const COACHING_LIMIT_KEYS = ['daily_messages', 'active_goals'] as const;
export type CoachingLimitKey = (typeof COACHING_LIMIT_KEYS)[number];

export type CoachingFeatureGroup = 'coach' | 'align' | 'manifest' | 'achieve';

/**
 * How a feature refuses a member who does not have it.
 *
 * `hard` answers 402 and the app shows the lock. `silent` never errors: the
 * member gets the built-in wording instead of the written one and is told
 * nothing, which is how the evening review has always behaved.
 */
export type CoachingFeatureGate = 'hard' | 'silent';

type AccessDef = {
  kind: 'access';
  group: CoachingFeatureGroup;
  /** Admin-facing row label. */
  label: string;
  /** Admin-facing one-liner under the label. */
  hint: string;
  gate: CoachingFeatureGate;
  defaultMinTier: CoachingFeatureTier;
  /** What today's Settings switches said, before a tenant saves the table. */
  legacy?: (c: LegacyCoachingKnobs) => CoachingFeatureTier;
  /** Eyebrow on the member's lock screen. */
  lockTitle: string;
  /** Body on the member's lock screen. */
  lockBody: string;
  /** The line this feature contributes to a plan's "what you get" list. */
  includedLine: string;
};

type LimitDef = {
  kind: 'limit';
  group: CoachingFeatureGroup;
  label: string;
  hint: string;
  /** Unit shown beside the number in admin. */
  unit: string;
  defaultLimits: Record<CoachingTier, number>;
  legacyLimits?: (c: LegacyCoachingKnobs) => Record<CoachingTier, number>;
  includedLine: (value: number) => string;
};

export type CoachingFeatureDef = AccessDef | LimitDef;

/**
 * The subset of the coaching config the table falls back to while no tenant
 * has saved one. Typed structurally so this module stays free of the whole
 * `TenantFeatures` tree.
 */
export type LegacyCoachingKnobs = {
  free_daily_messages: number;
  pro_daily_messages: number;
  free_active_goals: number;
  streak_freeze_pro: boolean;
  chart_guidance_pro_only: boolean;
  tools_pro_only: boolean;
};

const proWhen = (on: boolean): CoachingFeatureTier => (on ? 'pro' : 'free');

export const COACHING_FEATURES: Record<CoachingAccessKey | CoachingLimitKey, CoachingFeatureDef> = {
  journal_reflection: {
    kind: 'access',
    group: 'manifest',
    label: 'Reflect on a journal entry',
    hint: 'Master Kala writes back on an entry the member chooses.',
    gate: 'hard',
    defaultMinTier: 'pro',
    lockTitle: 'Reflections are part of Success Pro',
    lockBody:
      'Write as much as you like on any plan. A written reflection on an entry comes with Success Pro.',
    includedLine: 'Written reflections on your journal entries',
  },
  affirmation_generation: {
    kind: 'access',
    group: 'manifest',
    label: 'Write affirmations for a goal',
    hint: 'Three affirmations written for the member’s own goal.',
    gate: 'hard',
    defaultMinTier: 'pro',
    lockTitle: 'Written affirmations are part of Success Pro',
    lockBody:
      'You can write and save your own affirmations on any plan. Having three written for your goal comes with Success Pro.',
    includedLine: 'Affirmations written for your goal',
  },
  action_suggestions: {
    kind: 'access',
    group: 'achieve',
    label: 'Suggest next actions',
    hint: 'Suggested actions for the member’s goal.',
    gate: 'hard',
    defaultMinTier: 'pro',
    lockTitle: 'Suggested actions are part of Success Pro',
    lockBody: 'Add your own actions on any plan. Having them suggested comes with Success Pro.',
    includedLine: 'Suggested next actions',
  },
  evening_ai_summary: {
    kind: 'access',
    group: 'coach',
    label: 'Written evening summary',
    hint: 'The evening review closes with a written summary instead of the built-in wording.',
    gate: 'silent',
    defaultMinTier: 'pro',
    lockTitle: 'Written evening summaries are part of Success Pro',
    lockBody: 'Every member closes their day. Success Pro writes the summary for yours.',
    includedLine: 'An evening summary written for your day',
  },
  weekly_ai_summary: {
    kind: 'access',
    group: 'achieve',
    label: 'Written weekly summary',
    hint: 'The weekly review closes with a written summary.',
    gate: 'silent',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'Written weekly summaries are part of Success Pro',
    lockBody: 'Every member closes their week. Success Pro writes the summary for yours.',
    includedLine: 'A weekly summary written for your week',
  },
  chart: {
    kind: 'access',
    group: 'align',
    label: 'Birth chart',
    hint: 'The full natal chart and its written readings.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.chart_guidance_pro_only),
    lockTitle: 'Your birth chart is part of Success Pro',
    lockBody:
      'Astrology here is a reflective framework, not a prediction. Success Pro opens your full chart and its readings.',
    includedLine: 'Your full birth chart and readings',
  },
  guidance: {
    kind: 'access',
    group: 'align',
    label: 'Daily Vedic guidance',
    hint: 'Today’s theme, what it is best for, and what to take care with.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.chart_guidance_pro_only),
    lockTitle: 'Daily guidance is part of Success Pro',
    lockBody:
      'Astrology here is a reflective framework, not a prediction. Success Pro opens a reading for each day.',
    includedLine: 'Guidance for each day',
  },
  karma: {
    kind: 'access',
    group: 'align',
    label: 'Karma reading',
    hint: 'What the member falls back on, what they are growing toward, and a practice for it.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.chart_guidance_pro_only),
    lockTitle: 'The karma reading is part of Success Pro',
    lockBody:
      'Read from the same chart as your guidance, as a reflective framework and never a prediction. It comes with Success Pro.',
    includedLine: 'Your karma reading',
  },
  aura: {
    kind: 'access',
    group: 'align',
    label: 'Aura reading',
    hint: 'Computed from a five-question check-in, the member’s cycles and their practice. No photo.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.chart_guidance_pro_only),
    lockTitle: 'The aura reading is part of Success Pro',
    lockBody:
      'A mirror of the answers you give, never a scan of anything. It comes with Success Pro.',
    includedLine: 'Your aura reading',
  },
  palmistry: {
    kind: 'access',
    group: 'align',
    label: 'Palm reading',
    hint: 'The member photographs their palm. The photo is read once and never stored.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.chart_guidance_pro_only),
    lockTitle: 'The palm reading is part of Success Pro',
    lockBody:
      'A traditional reading of your own hand, offered for reflection. Your photo is read once and never saved. It comes with Success Pro.',
    includedLine: 'A reading of your own palm',
  },
  breathwork: {
    kind: 'access',
    group: 'align',
    label: 'Breathwork',
    hint: 'Guided breathing sessions.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'Breathwork is part of Success Pro',
    lockBody: 'Guided breathing sessions for calm, focus, energy and sleep come with Success Pro.',
    includedLine: 'Guided breathwork',
  },
  frequencies: {
    kind: 'access',
    group: 'align',
    label: 'Frequencies',
    hint: 'The listening library.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'Frequencies are part of Success Pro',
    lockBody:
      'Listening sessions are offered for reflection, never as treatment. They come with Success Pro.',
    includedLine: 'The frequency library',
  },
  biorhythm: {
    kind: 'access',
    group: 'align',
    label: 'Biorhythm',
    hint: 'The member’s cycles for the day.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'Biorhythm is part of Success Pro',
    lockBody:
      'Biorhythm is offered for personal reflection and is not an established predictor. It comes with Success Pro.',
    includedLine: 'Your biorhythm cycles',
  },
  mantras: {
    kind: 'access',
    group: 'align',
    label: 'Mantras',
    hint: 'The mantra library and the repetition counter.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'Mantras are part of Success Pro',
    lockBody:
      'Mantras are a traditional focusing practice, offered for reflection. The library comes with Success Pro.',
    includedLine: 'The mantra library',
  },
  meditation: {
    kind: 'access',
    group: 'align',
    label: 'Guided meditation',
    hint: 'Guided sessions the member is walked through a step at a time.',
    gate: 'hard',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'Guided meditation is part of Success Pro',
    lockBody:
      'Guided sessions for calm, focus and rest. They are a reflective practice, never treatment, and they come with Success Pro.',
    includedLine: 'Guided meditation sessions',
  },
  biorhythm_spiritual: {
    kind: 'access',
    group: 'align',
    label: 'Spiritual cycle',
    hint: 'A fourth 53-day cycle beside the three the biorhythm screen already shows.',
    gate: 'silent',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'The spiritual cycle is part of Success Pro',
    lockBody:
      'Every member sees their physical, emotional and intellectual cycles. Success Pro adds the fourth.',
    includedLine: 'Your spiritual cycle beside the other three',
  },
  biorhythm_lucky: {
    kind: 'access',
    group: 'align',
    label: 'Lucky colors and stone',
    hint: 'The colors and the crystal that go with the day\u2019s cycles.',
    gate: 'silent',
    defaultMinTier: 'free',
    legacy: (c) => proWhen(c.tools_pro_only),
    lockTitle: 'Lucky colors are part of Success Pro',
    lockBody:
      'Colors and stones are a reflective anchor for the day, never a prediction. They come with Success Pro.',
    includedLine: 'The day\u2019s lucky colors and stone',
  },
  vedic_glossary: {
    kind: 'access',
    group: 'align',
    label: 'Vedic glossary',
    hint: 'What a dasha, a nakshatra and a yoga are, and how to read the chart screens.',
    gate: 'silent',
    defaultMinTier: 'free',
    lockTitle: 'The glossary is part of Success Pro',
    lockBody: 'The words behind your reading, explained plainly. It comes with Success Pro.',
    includedLine: 'The glossary behind your reading',
  },
  mood_history: {
    kind: 'access',
    group: 'achieve',
    label: 'Mood over time',
    hint: 'The trend of what the member answered in their evening reviews. It asks nothing extra.',
    gate: 'silent',
    defaultMinTier: 'free',
    lockTitle: 'The mood trend is part of Success Pro',
    lockBody:
      'Every member is asked how the day felt. Success Pro charts those answers over weeks.',
    includedLine: 'Your mood over weeks, not just today',
  },
  miracle_wins: {
    kind: 'access',
    group: 'manifest',
    label: 'Miracle wins',
    hint: 'The member’s own log of what showed up. Private, and never totalled.',
    gate: 'silent',
    defaultMinTier: 'free',
    lockTitle: 'The wins log is part of Success Pro',
    lockBody:
      'Write down the good things as they arrive and keep the record. It comes with Success Pro.',
    includedLine: 'A log of the wins as they arrive',
  },
  manifestation_toolkit: {
    kind: 'access',
    group: 'manifest',
    label: 'Manifestation toolkit',
    hint: 'Tapping, the water blessing and quantum jumping, walked a step at a time.',
    gate: 'silent',
    defaultMinTier: 'free',
    lockTitle: 'The toolkit is part of Success Pro',
    lockBody:
      'Reflective practices drawn from popular traditions, never treatment. They come with Success Pro.',
    includedLine: 'The manifestation toolkit',
  },
  future_self: {
    kind: 'access',
    group: 'manifest',
    label: 'Future Self',
    hint: 'The member writes who they are growing into, and Master Kala can read it.',
    gate: 'silent',
    defaultMinTier: 'free',
    lockTitle: 'Future Self is part of Success Pro',
    lockBody:
      'Write the version of yourself today’s choices are for, and keep it. It comes with Success Pro.',
    includedLine: 'Your Future Self, written and kept',
  },
  alignment_todos: {
    kind: 'access',
    group: 'manifest',
    label: 'Daily alignment list',
    hint: 'Seven small practices drawn for the day and ticked off. Separate from the member’s actions.',
    gate: 'silent',
    defaultMinTier: 'free',
    lockTitle: 'The daily list is part of Success Pro',
    lockBody: 'Seven small things for today, drawn fresh each morning. It comes with Success Pro.',
    includedLine: 'A short alignment list each day',
  },
  streak_freeze: {
    kind: 'access',
    group: 'coach',
    label: 'Streak freeze',
    hint: 'One missed day a month is bridged automatically.',
    gate: 'silent',
    defaultMinTier: 'pro',
    legacy: (c) => (c.streak_freeze_pro ? 'pro' : 'off'),
    lockTitle: 'The streak freeze is part of Success Pro',
    lockBody: 'One missed day a month is bridged for you, so a long streak survives a hard week.',
    includedLine: 'One streak freeze a month',
  },
  daily_messages: {
    kind: 'limit',
    group: 'coach',
    label: 'Master Kala messages a day',
    hint: '0 means no limit.',
    unit: 'a day',
    defaultLimits: { free: 5, pro: 30, elite: 30 },
    legacyLimits: (c) => ({
      free: c.free_daily_messages,
      pro: c.pro_daily_messages,
      elite: c.pro_daily_messages,
    }),
    includedLine: (n) => (n <= 0 ? 'Unlimited messages with Master Kala' : `${n} messages a day`),
  },
  active_goals: {
    kind: 'limit',
    group: 'achieve',
    label: 'Goals at once',
    hint: '0 means no limit.',
    unit: 'at once',
    defaultLimits: { free: 1, pro: 0, elite: 0 },
    legacyLimits: (c) => ({ free: c.free_active_goals, pro: 0, elite: 0 }),
    includedLine: (n) => (n <= 0 ? 'As many goals as you want' : `${n} goals at once`),
  },
  prompt_library: {
    kind: 'access',
    group: 'coach',
    label: 'Prompt library',
    hint: 'Ready-made prompts a member can open a conversation with. Each prompt can also be held back for a plan on its own.',
    gate: 'silent',
    defaultMinTier: 'free',
    lockTitle: 'The prompt library is part of Success Pro',
    lockBody:
      'Ask Master Kala anything on any plan. The library of ready-made prompts, including the scripts, comes with Success Pro.',
    includedLine: 'A library of ready-made prompts and scripts for Master Kala',
  },
  programs: {
    kind: 'access',
    group: 'achieve',
    label: 'Guided programs',
    hint: 'Multi-day journeys with a read, a Master Kala prompt and a micro-action per day. Each program also carries its own plan.',
    gate: 'hard',
    defaultMinTier: 'free',
    lockTitle: 'Guided programs are part of Success Pro',
    lockBody:
      'A program walks you through a week or a month one day at a time: a short read, a question for Master Kala, and one thing to do. It comes with Success Pro.',
    includedLine: 'Guided day-by-day programs',
  },
};

/** What a tenant has saved. Anything absent falls back, key by key. */
export type CoachingPlanFeatures = {
  access?: Partial<Record<CoachingAccessKey, CoachingFeatureTier>>;
  limits?: Partial<Record<CoachingLimitKey, Partial<Record<CoachingTier, number>>>>;
};

export type ResolvedCoachingFeatures = {
  access: Record<CoachingAccessKey, CoachingFeatureTier>;
  limits: Record<CoachingLimitKey, Record<CoachingTier, number>>;
};

const isAccessDef = (d: CoachingFeatureDef): d is AccessDef => d.kind === 'access';

export function isCoachingAccessKey(key: string): key is CoachingAccessKey {
  return (COACHING_ACCESS_KEYS as readonly string[]).includes(key);
}
export function isCoachingLimitKey(key: string): key is CoachingLimitKey {
  return (COACHING_LIMIT_KEYS as readonly string[]).includes(key);
}

/**
 * The table in force: what the tenant saved, else what today's Settings
 * switches said, else the catalog default. Unknown keys and tiers in a saved
 * table are ignored rather than trusted, so an older saved shape cannot widen
 * access after the catalog changes.
 */
export function resolveCoachingPlanFeatures(
  knobs: LegacyCoachingKnobs,
  saved?: CoachingPlanFeatures | null,
): ResolvedCoachingFeatures {
  const access = {} as Record<CoachingAccessKey, CoachingFeatureTier>;
  for (const key of COACHING_ACCESS_KEYS) {
    const def = COACHING_FEATURES[key];
    if (!isAccessDef(def)) continue;
    const stored = saved?.access?.[key];
    access[key] =
      stored && (COACHING_FEATURE_TIERS as readonly string[]).includes(stored)
        ? stored
        : (def.legacy?.(knobs) ?? def.defaultMinTier);
  }

  const limits = {} as Record<CoachingLimitKey, Record<CoachingTier, number>>;
  for (const key of COACHING_LIMIT_KEYS) {
    const def = COACHING_FEATURES[key];
    if (isAccessDef(def)) continue;
    const base = def.legacyLimits?.(knobs) ?? def.defaultLimits;
    const stored = saved?.limits?.[key];
    limits[key] = { ...base };
    if (stored) {
      for (const tier of COACHING_TIERS) {
        const value = stored[tier];
        if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
          limits[key][tier] = Math.floor(value);
        }
      }
    }
  }

  return { access, limits };
}

/** The lowest plan that has `key`, or `off` when nobody does. */
export function featureMinTier(
  resolved: ResolvedCoachingFeatures,
  key: CoachingAccessKey,
): CoachingFeatureTier {
  return resolved.access[key];
}

/** Does a member on `tier` have `key`? */
export function featureAllowed(
  resolved: ResolvedCoachingFeatures,
  tier: CoachingTier,
  key: CoachingAccessKey,
): boolean {
  const min = resolved.access[key];
  if (min === 'off') return false;
  return COACHING_TIER_RANK[tier] >= COACHING_TIER_RANK[min];
}

/** Is `key` free for everyone? Lets a caller skip looking the tier up at all. */
export function featureIsFree(resolved: ResolvedCoachingFeatures, key: CoachingAccessKey): boolean {
  return resolved.access[key] === 'free';
}

/** The limit for `tier`, or null for no limit. */
export function featureLimit(
  resolved: ResolvedCoachingFeatures,
  tier: CoachingTier,
  key: CoachingLimitKey,
): number | null {
  const value = resolved.limits[key][tier];
  return value > 0 ? value : null;
}

/**
 * What a plan adds over the one below it, as member-facing lines. This is what
 * the upgrade page, the paywall and Master Kala's context all read, so none of
 * them carries its own hardcoded list that can drift from what the tenant set.
 */
export function planFeatureSummary(
  resolved: ResolvedCoachingFeatures,
  tier: CoachingTier,
): string[] {
  const below = COACHING_TIERS[COACHING_TIER_RANK[tier] - 1];
  const lines: string[] = [];

  for (const key of COACHING_ACCESS_KEYS) {
    const def = COACHING_FEATURES[key];
    if (!isAccessDef(def)) continue;
    if (!featureAllowed(resolved, tier, key)) continue;
    if (below && featureAllowed(resolved, below, key)) continue;
    lines.push(def.includedLine);
  }

  for (const key of COACHING_LIMIT_KEYS) {
    const def = COACHING_FEATURES[key];
    if (isAccessDef(def)) continue;
    const mine = resolved.limits[key][tier];
    if (below !== undefined && resolved.limits[key][below] === mine) continue;
    lines.push(def.includedLine(mine));
  }

  return lines;
}
